import React, { useState, useMemo } from 'react';
import { HeagleRenderer } from '../types';

interface RendererTableProps {
  renderers: HeagleRenderer[];
}

type SortColumn = 'renderer_id' | 'renderer_group' | null;
type SortDirection = 'asc' | 'desc';

const RendererTable: React.FC<RendererTableProps> = ({ renderers }) => {
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (column: 'renderer_id' | 'renderer_group') => {
    if (sortColumn === column) {
      // 같은 컬럼 클릭 시 정렬 방향 토글
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // 다른 컬럼 클릭 시 오름차순으로 설정
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedRenderers = useMemo(() => {
    if (!sortColumn) return renderers;

    return [...renderers].sort((a, b) => {
      let aValue: number | string;
      let bValue: number | string;

      if (sortColumn === 'renderer_id') {
        aValue = a.renderer_id;
        bValue = b.renderer_id;
      } else {
        // renderer_group
        aValue = a.renderer_group || '';
        bValue = b.renderer_group || '';
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [renderers, sortColumn, sortDirection]);

  const getSortIcon = (column: 'renderer_id' | 'renderer_group') => {
    if (sortColumn !== column) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="renderer-table-container">
      <table className="renderer-table">
        <thead>
          <tr>
            <th 
              className="sortable" 
              onClick={() => handleSort('renderer_id')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              Renderer ID {getSortIcon('renderer_id')}
            </th>
            <th 
              className="sortable" 
              onClick={() => handleSort('renderer_group')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              Renderer Group {getSortIcon('renderer_group')}
            </th>
            <th>Renderer Name</th>
            <th>Renderer Port</th>
            <th>Max Connections</th>
            <th>클라이언트 접속수</th>
          </tr>
        </thead>
        <tbody>
          {sortedRenderers.map((renderer) => {
            const isWaiting = renderer.renderer_name === '(접속 대기 중)';
            return (
              <tr key={renderer.renderer_id} className={isWaiting ? 'waiting' : ''}>
                <td>{renderer.renderer_id}</td>
                <td>{renderer.renderer_group || '-'}</td>
                <td>{renderer.renderer_name}</td>
                <td>{renderer.renderer_port || '-'}</td>
                <td>{renderer.max_conn || '-'}</td>
                <td>{renderer.client_count}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default RendererTable;
